import SiteLayout from "@/components/feature/SiteLayout";
import PageHero from "@/components/feature/PageHero";
import Reveal from "@/components/base/Reveal";
import Seo from "@/components/base/Seo";
import SectionHeading from "@/components/feature/SectionHeading";
import FeatureGrid from "@/components/feature/FeatureGrid";
import ProcessSteps from "@/components/feature/ProcessSteps";
import FaqAccordion from "@/components/feature/FaqAccordion";
import FinalCta from "@/pages/home/components/FinalCta";
import RevendaPlans from "@/pages/revenda/components/RevendaPlans";
import { whatsappLink } from "@/constants/site";
import {
  revendaBenefits,
  partnerConditions,
  partnerSteps,
  revendaFaqs,
} from "@/mocks/revenda";

export default function Revenda() {
  return (
    <SiteLayout>
      <Seo
        title="Revenda de Hospedagem para Parceiros e Agências | NXHOST"
        description="Programa de revenda da NXHOST: revenda hospedagem com preços especiais de parceiro, painel de gestão de clientes, sua marca no atendimento e suporte dedicado ao revendedor."
        keywords="revenda de hospedagem, revenda de sites, programa de parceiros, revendedor de hospedagem, white label, agência"
        path="/revenda"
        titleEn="Hosting Reseller Program for Partners and Agencies | NXHOST"
        titleEs="Programa de Reventa de Hosting para Socios y Agencias | NXHOST"
        descriptionEn="NXHOST reseller program: resell hosting with special partner pricing, a client management panel, your own brand and dedicated reseller support."
        descriptionEs="Programa de reventa de NXHOST: revende hosting con precios especiales para socios, panel de gestión de clientes, tu marca y soporte dedicado al revendedor."
        breadcrumbs={[
          { name: "Início", path: "/" },
          { name: "Revenda", path: "/revenda" },
        ]}
        faqs={revendaFaqs}
      />

      <PageHero
        badge="Programa de revenda"
        badgeIcon="ri-refund-2-line"
        title="Revenda hospedagem com a infraestrutura da NXHOST."
        subtitle="Um programa pensado para agências, desenvolvedores e parceiros que querem oferecer hospedagem e soluções digitais para os seus próprios clientes, com preços especiais e a sua marca no atendimento."
        crumbs={[{ label: "Início", to: "/" }, { label: "Revenda" }]}
      >
        <Reveal delay={220}>
          <div className="rounded-2xl border border-background-50/10 bg-foreground-900/60 p-5 backdrop-blur-sm">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wide text-background-400">
                Painel do parceiro
              </span>
              <span className="flex items-center gap-1.5 text-[11px] font-medium text-secondary-400">
                <span className="h-2 w-2 rounded-full bg-secondary-400"></span>
                Ativo
              </span>
            </div>

            <div className="mt-4">
              <span className="text-sm text-background-400">a partir de</span>
            </div>
            <div className="mt-1 flex items-end gap-1">
              <span className="text-sm font-medium text-background-300">R$</span>
              <span className="font-heading text-5xl font-bold text-background-50">55,90</span>
              <span className="mb-2 text-sm text-background-300">/mês</span>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-background-50/10 bg-background-50/5 p-3">
                <p className="text-[11px] uppercase tracking-wide text-background-400">Contas</p>
                <p className="mt-1 font-heading text-lg font-bold text-background-50">5 a +100</p>
              </div>
              <div className="rounded-lg border border-background-50/10 bg-background-50/5 p-3">
                <p className="text-[11px] uppercase tracking-wide text-background-400">Sua marca</p>
                <p className="mt-1 font-heading text-lg font-bold text-background-50">Sim</p>
              </div>
            </div>
          </div>
        </Reveal>
      </PageHero>

      <RevendaPlans />

      <section className="bg-background-50 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <SectionHeading
            badge="Por que revender"
            badgeIcon="ri-hand-coin-line"
            badgeTone="accent"
            title="Mais do que hospedagem. Um negócio para o seu parceiro."
            description="Você foca nos seus clientes e no seu relacionamento. A infraestrutura e o suporte de base ficam por nossa conta."
          />
          <div className="mt-12">
            <FeatureGrid items={revendaBenefits} columns={3} accent="accent" />
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <Reveal>
              <div>
                <span className="inline-flex items-center gap-2 rounded-full bg-secondary-50 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-secondary-700">
                  <i className="ri-shake-hands-line" aria-hidden="true"></i>
                  Condições para parceiros
                </span>
                <h2 className="mt-5 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-4xl">
                  Tudo o que você recebe como parceiro NXHOST.
                </h2>
                <p className="mt-4 text-base leading-relaxed text-foreground-600">
                  Condições comerciais pensadas para você revender com margem e atender os seus
                  clientes com profissionalismo.
                </p>

                <ul className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {partnerConditions.map((condition) => (
                    <li
                      key={condition}
                      className="flex items-start gap-2 text-sm text-foreground-700"
                    >
                      <i
                        className="ri-check-double-line mt-0.5 text-secondary-600"
                        aria-hidden="true"
                      ></i>
                      {condition}
                    </li>
                  ))}
                </ul>

                <a
                  href={whatsappLink("Ola! Quero ser parceiro de revenda da NXHOST e conhecer as condicoes.")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-9 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  Quero conhecer as condições
                  <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </a>
              </div>
            </Reveal>

            <Reveal delay={120}>
              <div className="overflow-hidden rounded-2xl border border-background-200 bg-background-50">
                <img
                  src="https://readdy.ai/api/search-image?query=business%20partners%20collaborating%20in%20a%20modern%20bright%20office%20reviewing%20a%20hosting%20control%20panel%20dashboard%20on%20a%20laptop%2C%20professional%20corporate%20technology%20atmosphere%2C%20clean%20minimal%20workspace%2C%20soft%20neutral%20background&width=1100&height=800&seq=nh-revenda-partners&orientation=landscape"
                  alt="Parceiros do programa de revenda de hospedagem NXHOST gerenciando clientes em um painel"
                  title="Programa de revenda de hospedagem NXHOST — gestão de clientes"
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-foreground-950 py-16 text-background-50 md:py-24">
        <div className="absolute inset-0 nx-grid opacity-25"></div>
        <div className="nx-glow absolute -bottom-20 right-1/4 h-[420px] w-[420px] animate-pulseGlow"></div>
        <div className="relative mx-auto max-w-7xl px-4 md:px-8">
          <SectionHeading
            badge="Como se tornar parceiro"
            badgeIcon="ri-route-line"
            title="Do primeiro contato ao seu negócio de revenda."
            description="Um caminho simples para você começar a revender com a NXHOST."
            tone="dark"
          />
          <div className="mt-12">
            <ProcessSteps steps={partnerSteps} tone="dark" />
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <SectionHeading
            badge="Dúvidas frequentes"
            badgeIcon="ri-question-line"
            title="Perguntas sobre o programa de revenda."
          />
          <div className="mt-12">
            <FaqAccordion items={revendaFaqs} />
          </div>
        </div>
      </section>

      <FinalCta />
    </SiteLayout>
  );
}